import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useCreateComment, useUpdateComment } from '@/hooks/apiHooks';
import LexicalCommentEditor, {
  CommentEditorRef,
  CommentImageUploadMode,
} from '@/components/LexicalEditor/CommentEditor';
import AttachmentIcon from '@/public/icons/attatchment.svg';
import MentionIcon from '@/public/icons/mention.svg';
import { RemoteFile } from '@/components/file-thumbnail/utils';
import {
  CancelButton,
  Footer,
  FormButtonWrapper,
  SubmitButton,
  UtilButton,
  Wrapper,
} from '@/components/Comment/CommentInput/styled';
import classNames from 'classnames';
import { BoardDetailCommentProps } from '@/components/Board/BoardDetail/Feedback/BoardDetailComment';
import { isIOS } from '@/utils/userEnv';
import { t } from 'i18next';
import { useSnackbar } from '@/hooks/global-tools';
import {
  extractMentions,
  extractPassportIds,
} from '@/components/Comment/CommentInput/index.util';

export type CommentInputProps = Pick<
  BoardDetailCommentProps,
  'inputStyle' | 'service' | 'beforeSubmit'
> & {
  readonly id?: string;

  readonly commentGroupId: string;
  readonly updateCommentList?: () => Promise<void>;
  readonly disabled?: boolean;

  readonly commentId?: string;
  readonly initialData?: {
    readonly content: string;
    readonly attachments: RemoteFile[];
  };
  readonly onSuccess?: () => Promise<void>;
  readonly onCancel?: () => Promise<void>;
  readonly folding: boolean;
  readonly onBlur?: () => void;
  readonly onFocus?: () => void;
  /**
   * 파일 첨부 버튼에 표시할 아이콘.
   * 품의(approval) 등 영역에서 사진 아이콘 등으로 override 할 때 사용.
   */
  readonly attachmentIcon?: React.ReactNode;
  /**
   * (선택) 작성 호출을 외부 함수로 override.
   * 미전달 시 기본 `useCreateComment` (portal `/api/v1/comment/create`) 사용.
   * 품의 영역에서 품의 작성 API 로 분기하기 위함.
   */
  readonly createCommentOverride?: (payload: {
    commentGroupId: string;
    content: string;
    mentions: string[];
    attachments: { name: string; link: string }[];
  }) => Promise<void>;
  /**
   * (선택) 수정 호출을 외부 함수로 override.
   * 미전달 시 기본 `useUpdateComment` (portal POST `/api/v1/comment/content`) 사용.
   * 품의 영역에서 품의 수정 API 로 분기하기 위함.
   */
  readonly updateCommentOverride?: (payload: {
    commentId: string;
    content: string;
    mentions: string[];
    attachments: { name: string; link: string }[];
  }) => Promise<void>;
  /**
   * (선택) 에디터 초기 HTML — 드래프트 복원에 사용. `initialData` 와 별개.
   * `initialData` 가 있으면(수정 모드) 그 쪽이 우선.
   */
  readonly initialContent?: string;
  /**
   * (선택) 에디터 onChange 외부 노출. 드래프트 autosave debounce 호출처가 외부에서 처리.
   */
  readonly onContentChange?: (html: string) => void;
  /**
   * 이미지 첨부 처리 방식. 미전달 시 'file'(첨부 카드) — 게시판/소셜 기존 동작.
   * 품의 코멘트는 'inline' 패스 → 본문 인라인 이미지 + 크기 조절.
   */
  readonly imageUploadMode?: CommentImageUploadMode;
  /**
   * 전자결재 코멘트 여부. 미전달 시 false — 게시판/마케팅/소셜 기존 동작.
   * true 면 멘션 팀명 항상 포함 + 레거시 결재 뷰어 호환 마크업.
   */
  readonly isApproval?: boolean;
  /**
   * (선택) 초기 포커스(블랙 보더) 상태 override. 미전달 시 기존 동작(`!folding`) 유지.
   * 품의 코멘트는 `false` 패스 → 진입 시 연그레이 보더, 실제 클릭/포커스 시에만 블랙.
   */
  readonly defaultFocused?: boolean;
};

export type CommentInputRef = {
  submit: () => Promise<void>;
  /**
   * 작성 중 판정 여부 반환. true 면 작성 중, false 면 작성 중 아님으로 간주.
   * - 작성(create) 모드: 에디터에 내용이 있거나 이미지 업로드 중이면 true.
   * - 수정(edit) 모드: 인라인 수정창이 떠 있는 것 자체를 작성 중으로 본다(true).
   */
  hasContent: () => boolean;
};

const CommentInput = forwardRef<CommentInputRef, CommentInputProps>(
  (
    {
      commentGroupId,
      updateCommentList,
      id = 'comment-input-box',
      commentId,
      initialData,
      disabled,
      onSuccess,
      onCancel,
      folding,
      onBlur,
      onFocus,
      inputStyle,
      service,
      beforeSubmit,
      attachmentIcon,
      createCommentOverride,
      updateCommentOverride,
      initialContent,
      onContentChange,
      imageUploadMode,
      isApproval,
      defaultFocused,
    },
    ref,
  ) => {
    const mode = initialData ? 'edit' : 'create';
    const buttonTitle = mode === 'edit' ? t('저장') : t('등록');
    const hasCancelButton = mode === 'edit';

    const containerRef = useRef<HTMLDivElement>(null);
    const editorRef = useRef<CommentEditorRef>(null);
    // 수정 모드에서 초기 데이터 로드 완료 여부
    const isInitialDataLoadedRef = useRef(false);

    const { showSuccess, showErrorMessage } = useSnackbar();

    const [hasFocus, setHasFocus] = useState(defaultFocused ?? !folding);
    const [comments, setComments] = useState('');
    const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);

    const isEmpty = editorRef.current?.isEmpty?.() ?? true;
    const isButtonDisabled =
      isEmpty ||
      comments.includes('data-status="loading"') ||
      editorRef.current?.hasUploadingImage?.();

    const styleClass = classNames({
      focus: hasFocus && inputStyle !== 'fixed-not-focus',
      'top-style': inputStyle === 'top',
      'fixed-style': inputStyle === 'fixed' || inputStyle === 'fixed-not-focus',
      'mobile-style': inputStyle === 'mobile',
    });

    const handleOnChange = (value: string) => {
      setComments(value);
      // 외부 autosave 호출처 (선택)
      onContentChange?.(value);
    };

    const reset = () => {
      setComments('');
      isInitialDataLoadedRef.current = false;

      editorRef.current?.clear?.();
      onCancel?.();
    };

    const submitComment = async () => {
      try {
        setIsCommentSubmitting(true);

        const trimmedContent = comments.trim();
        const mentions = extractPassportIds(comments);
        const payload = {
          commentGroupId,
          content: trimmedContent,
          mentions,
          service,
        };

        if (mode === 'edit') {
          // override 우선. 미전달 시 portal 기본 동작 그대로.
          if (updateCommentOverride) {
            if (!commentId) {
              throw new Error('commentId is required for edit');
            }
            await updateCommentOverride({
              commentId,
              content: trimmedContent,
              mentions,
              attachments: [],
            });
          } else {
            await useUpdateComment({
              commentId,
              ...payload,
            });
          }
        } else {
          if (createCommentOverride) {
            await createCommentOverride({
              commentGroupId,
              content: trimmedContent,
              mentions,
              attachments: [],
            });
          } else {
            await useCreateComment(payload);
          }
        }

        showSuccess({
          message: isApproval
            ? mode === 'edit'
              ? t('코멘트가 수정되었습니다.')
              : t('코멘트가 등록되었습니다.')
            : mode === 'edit'
            ? t('Comment.댓글이 저장되었습니다.')
            : t('Comment.댓글이 등록되었습니다.'),
        });

        await updateCommentList?.();
        reset();
        onSuccess?.();
      } catch (e) {
        showErrorMessage(e);
      } finally {
        setIsCommentSubmitting(false);
      }
    };

    const submitButtonOnClick = async () => {
      const mentionedIds = extractPassportIds(comments);
      if (beforeSubmit) {
        // 풀 멘션 객체(employeeNumber/email 포함)는 하위호환 optional 인자로 전달.
        const mentions = extractMentions(comments);
        const canProceed = await beforeSubmit(mentionedIds, mentions);
        if (!canProceed) {
          return;
        }
      }

      await submitComment();
    };

    useEffect(() => {
      // 수정 모드일 때만 초기 데이터 로드
      // 이미 로드된 경우는 스킵 (커서 튐 방지)
      if (initialData && !isInitialDataLoadedRef.current) {
        editorRef.current?.setData?.(initialData.content);
        isInitialDataLoadedRef.current = true;
      }
    }, [initialData?.content]);

    // (선택) 작성 모드 드래프트 복원 — `initialContent` 가 주어지면 1회만 set.
    // `initialData`(수정 모드)가 있으면 그쪽이 우선이므로 skip.
    useEffect(() => {
      if (initialData) return;
      if (isInitialDataLoadedRef.current) return;
      if (!initialContent) return;
      editorRef.current?.setData?.(initialContent);
      setComments(initialContent);
      isInitialDataLoadedRef.current = true;
      // initialContent 자체가 변경되면 다시 호출되지만, 가드로 1회만 set.
    }, [initialContent]);

    // 수정 모드 종료 시 초기화 플래그 리셋
    useEffect(() => {
      if (!initialData) {
        isInitialDataLoadedRef.current = false;
      }
    }, [initialData]);

    useEffect(() => {
      const handleWindowBlur = () => {
        //DO NOTHING
      };

      window.addEventListener('blur', handleWindowBlur);

      return () => {
        window.removeEventListener('blur', handleWindowBlur);
      };
    }, []);

    useEffect(() => {
      const handleVisualViewportResize = () => {
        if (
          isIOS(window.navigator.userAgent) &&
          window.visualViewport?.height &&
          window.visualViewport.height > 600
        ) {
          setHasFocus(false);
          onBlur?.();
        }
      };

      window.visualViewport?.addEventListener(
        'resize',
        handleVisualViewportResize,
      );

      return () => {
        window.visualViewport?.removeEventListener(
          'resize',
          handleVisualViewportResize,
        );
      };
    }, [onBlur]);

    useImperativeHandle(ref, () => ({
      submit: submitComment,
      hasContent: () => {
        // 수정 모드(인라인 edit) 활성 = 작성 중. 작성 모드는 에디터 내용 유무로 판정.
        if (mode === 'edit') return true;
        return editorRef.current?.hasContent?.() ?? false;
      },
    }));

    return (
      <Wrapper
        id={id}
        ref={containerRef}
        tabIndex={-1}
        onFocus={() => setHasFocus(true)}
        onBlur={(e) => {
          const next = (e as React.FocusEvent)
            .relatedTarget as HTMLElement | null;

          if (next && containerRef.current?.contains(next)) {
            return;
          }

          if (next) {
            setHasFocus(false);
          }
        }}
        className={styleClass}
      >
        <LexicalCommentEditor
          onChange={handleOnChange}
          value={comments}
          ref={editorRef}
          disabled={disabled || isCommentSubmitting}
          onBlur={onBlur}
          onFocus={onFocus}
          imageUploadMode={imageUploadMode}
          isApproval={isApproval}
          placeholder={isApproval ? t('코멘트를 입력해주세요.') : undefined}
        />
        <Footer direction="row" className={'footer'}>
          <UtilButton
            onClick={(e) => {
              editorRef.current?.openFile();
            }}
            aria-label="Insert Image"
            title="Insert Image"
            type="button"
          >
            {attachmentIcon ?? <AttachmentIcon />}
          </UtilButton>
          <UtilButton
            onClick={() => {
              editorRef.current?.insertMention?.();
            }}
            aria-label="Tag someone"
            title="Tag someone"
            type="button"
          >
            <MentionIcon />
          </UtilButton>
          <FormButtonWrapper direction="row" gap="4px">
            {hasCancelButton && (
              <CancelButton
                disableRipple
                onClick={() => {
                  reset();
                  onCancel?.();
                }}
              >
                {t('취소')}
              </CancelButton>
            )}
            <SubmitButton
              disableRipple
              disabled={isButtonDisabled}
              loading={isCommentSubmitting}
              onClick={submitButtonOnClick}
            >
              {isCommentSubmitting ? '' : buttonTitle}
            </SubmitButton>
          </FormButtonWrapper>
        </Footer>
      </Wrapper>
    );
  },
);

CommentInput.displayName = 'CommentInput';
export default CommentInput;
